// Basketball — Today's Slate. Ported from the CLAUDE DESIGN prototype
// (bb-overview.jsx → SlateScreen + GameCard), wired to the real ingested board
// (useBasketballBoard → live Pinnacle moneyline / spread / total). HONEST: real
// odds only, never a model price or pick. Empty board → bb-await-grand. Loading
// → <Loading/>. Error → error pill + dormant beneath.
//
// The prototype game shape (away.code/name/city + rest days) maps onto the real
// `home`/`away` strings via teamCode()/teamColor(); the board carries no
// rest-day feed yet, so the rest chip renders its honest @/H fallback.

import { AwaitPanel, BbHead, BIcon, Loading, fmtOdds, fmtLine, fmtTip, shade } from "./_bbkit.tsx";
import { teamCode, teamColor } from "./teamColors.ts";
import { useBasketballBoard, type BballGame } from "./useBasketballBoard.ts";

function RestChip({ home }: { home?: boolean }): JSX.Element {
  // No rest-day feed on the board yet — render the honest home/away marker
  // (prototype's `days === undefined` branch).
  return (
    <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", fontSize: 11, fontWeight: home ? 600 : 400 }}>
      {home ? "H" : "@"}
    </span>
  );
}

export function GameCard({ game }: { game: BballGame }): JSX.Element {
  const awayCode = teamCode(game.away);
  const homeCode = teamCode(game.home);
  const awayColor = teamColor(awayCode);
  const homeColor = teamColor(homeCode);

  return (
    <div className="bb-game">
      <div className="bb-game-head">
        <span className="league">{game.league ?? "—"}</span>
        <span className="tip">{fmtTip(game.tipoffAt)}</span>
      </div>
      <div className="bb-game-teams">
        <div className="bb-team-row">
          <div className="bb-team-badge" style={{ background: `linear-gradient(135deg, ${awayColor} 0%, ${shade(awayColor, -30)} 100%)`, color: "#fff" }}>
            {awayCode}
          </div>
          <div className="name">{game.away}</div>
          <RestChip />
        </div>
        <div className="bb-team-row">
          <div className="bb-team-badge" style={{ background: `linear-gradient(135deg, ${homeColor} 0%, ${shade(homeColor, -30)} 100%)`, color: "#fff" }}>
            {homeCode}
          </div>
          <div className="name">{game.home}</div>
          <RestChip home />
        </div>
      </div>
      <div className="bb-markets">
        <div className="bb-market">
          <div className="mk">Moneyline</div>
          <div className="line">&nbsp;</div>
          <div className="odds"><span className="lbl">A</span><span className="o">{fmtOdds(game.moneyline.away)}</span></div>
          <div className="odds"><span className="lbl">H</span><span className="o">{fmtOdds(game.moneyline.home)}</span></div>
        </div>
        <div className="bb-market">
          <div className="mk">Spread</div>
          <div className="line">H {fmtLine(game.spread.homeLine, true)}</div>
          <div className="odds"><span className="lbl">A</span><span className="o dim">{fmtOdds(game.spread.away)}</span></div>
          <div className="odds"><span className="lbl">H</span><span className="o dim">{fmtOdds(game.spread.home)}</span></div>
        </div>
        <div className="bb-market">
          <div className="mk">Total</div>
          <div className="line">{fmtLine(game.total.line)}</div>
          <div className="odds"><span className="lbl">O</span><span className="o">{fmtOdds(game.total.over)}</span></div>
          <div className="odds"><span className="lbl">U</span><span className="o">{fmtOdds(game.total.under)}</span></div>
        </div>
      </div>
      <div className="bb-game-foot">
        <BIcon name="target" size={12} />
        <span>Pinnacle · <span className="lock">close-line tracked</span></span>
      </div>
    </div>
  );
}

export function SlateScreen(): JSX.Element {
  const { loading, error, games } = useBasketballBoard();
  const dormant = !loading && !error && games.length === 0;

  return (
    <div className="bb" data-screen-label="slate">
      <BbHead kicker="TODAY'S SLATE" title="Basketball" />

      <div className="bb-section-title">
        <h3>The board</h3>
        <div className="sub">{games.length} games · Pinnacle · close-line tracked</div>
      </div>

      {loading ? (
        <Loading label="Loading slate…" />
      ) : (
        <>
          {error ? <AwaitPanel kicker="ERROR" icon="alert" title="Couldn't load the slate" body={error} ghosts={4} /> : null}
          {dormant || error ? (
            <div className="bb-await-grand">
              <div className="k">NO GAMES · TODAY'S SLATE</div>
              <h3>No games on the board right now.</h3>
              <p>When basketball fixtures are in-window, their live Pinnacle moneyline, spread and total appear here — sourced through the same pipeline as football, with closing lines tracked for CLV.</p>
            </div>
          ) : (
            <div className="bb-slate">
              {games.map((g) => <GameCard key={g.fixtureId} game={g} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
